<script>
/**
 * Every industry pack on the platform.
 *
 * A pack is what one trade's customers sound like. It is listed here rather
 * than on a brand's page because one pack serves every brand whose Industry
 * field names it — editing it from inside one casino would change fifty, and
 * the person doing it would have no way to see that.
 */
import Layout from '@/views/layouts/main'
import PageHeader from '@/components/page-header'
import i18n from '@/i18n'
import { sharedHelper } from '@/utils'
import { tonePackService } from '@/helpers/services/tonePack.service'

export default {
  components: { Layout, PageHeader },
  data() {
    return {
      packs: [],
      loading: true,
      creating: false,
      newName: '',
    }
  },
  computed: {
    items() {
      return [{ text: i18n.t('module.admin') }, { text: i18n.t('module.tonePack'), active: true }]
    },
    /** Lowercase, digits, underscore — the same shape ai_bot stores. */
    newNameOk() {
      return /^[a-z][a-z0-9_]{0,30}$/.test(this.newName.trim())
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      this.loading = true
      try {
        const res = await tonePackService.list()
        this.packs = res.data || []
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(i18n.t('message.error'))
      } finally {
        this.loading = false
      }
    },
    open(name) {
      this.$router.push({ name: 'Industry-pack-detail', params: { name } })
    },
    /** A new pack starts empty and is filled on its own page. */
    async create() {
      const name = this.newName.trim()
      if (!this.newNameOk || this.creating) return
      this.creating = true
      try {
        await tonePackService.save(name, { glossary: [], examples: [], copy: {} })
        this.newName = ''
        this.open(name)
      } catch (e) {
        sharedHelper.alertErrorMessageWithoutCode(this.readError(e))
      } finally {
        this.creating = false
      }
    },
    readError(e) {
      const d = e && e.response && e.response.data
      return (d && (d.error || d.message)) || i18n.t('message.error')
    },
    when(iso) {
      if (!iso) return '—'
      return new Date(iso).toLocaleString()
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('module.tonePack')" :items="items" />

    <div class="card">
      <div class="card-body">
        <p class="small text-muted mb-3">{{ $t('tonePack.listHint') }}</p>

        <div v-if="loading" class="small text-muted">{{ $t('label.loading') }}</div>
        <div v-else-if="!packs.length" class="small text-muted">{{ $t('tonePack.empty') }}</div>

        <div v-else class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>{{ $t('tonePack.name') }}</th>
                <th>{{ $t('tonePack.description') }}</th>
                <th class="text-right">{{ $t('tonePack.glossaryCount') }}</th>
                <th class="text-right">{{ $t('tonePack.examplesCount') }}</th>
                <th>{{ $t('tonePack.updated') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in packs" :key="p.name" class="pack-row" @click="open(p.name)">
                <td><code>{{ p.name }}</code></td>
                <td class="text-muted small">{{ p.description || '—' }}</td>
                <td class="text-right">{{ p.glossary }}</td>
                <td class="text-right">{{ p.examples }}</td>
                <td class="text-muted small">{{ when(p.updatedAt) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <hr />
        <div class="d-flex align-items-center flex-wrap">
          <b-form-input v-model="newName" class="mr-2 new-name" placeholder="igaming" />
          <b-button variant="outline-primary" size="sm" :disabled="!newNameOk || creating" @click="create">
            {{ creating ? $t('label.loading') : $t('tonePack.create') }}
          </b-button>
          <small class="form-text text-muted w-100 mt-2">{{ $t('tonePack.createHint') }}</small>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.pack-row { cursor: pointer; }
.pack-row:hover { background: #f6f7fb; }
.new-name { max-width: 240px; }
</style>
